/**
 * Fake lead rooms (가짜 리딩방) config - Sprint 3a highlight.
 * Each room has a HIDDEN accuracy (never shown in UI) and a personality.
 * The player learns a room's reliability only by watching its call history.
 *
 * Personalities:
 *  - honest      : accuracy roughly matches its hit rate; calls are straight.
 *  - hype        : loud, over-promises ("2x guaranteed"); mediocre accuracy.
 *  - collusion   : occasionally lures buyers right before a whale dump
 *                  (the trap - a "get in now" call that is actually exit liquidity).
 *
 * VIP rooms are high-accuracy but gated behind a rewarded ad (entry pass).
 * Free rooms are always open but noisy/low-accuracy.
 */

export type RoomPersonality = 'honest' | 'hype' | 'collusion';

export interface LeadRoomConfig {
  id: string;
  /** i18n key = 'room_' + id */
  nameKey: string;
  /** i18n key for the room master's tagline */
  taglineKey: string;
  personality: RoomPersonality;
  /** hidden success probability of a call (0.3 ~ 0.85) - NEVER shown */
  accuracy: number;
  /** VIP rooms require a rewarded ad to enter (higher accuracy) */
  vip: boolean;
  /** for collusion rooms: chance a given call is actually a dump trap (0 for others) */
  trapChance: number;
  /** accent color (CSS hex) */
  color: string;
}

export const LEAD_ROOMS: LeadRoomConfig[] = [
  {
    id: 'newbie',
    nameKey: 'room_newbie',
    taglineKey: 'room_newbie_tag',
    personality: 'hype',
    accuracy: 0.34,
    vip: false,
    trapChance: 0,
    color: '#8A9CB8',
  },
  {
    id: 'diamond',
    nameKey: 'room_diamond',
    taglineKey: 'room_diamond_tag',
    personality: 'honest',
    accuracy: 0.58,
    vip: false,
    trapChance: 0,
    color: '#3D7BFF',
  },
  {
    id: 'insider',
    nameKey: 'room_insider',
    taglineKey: 'room_insider_tag',
    personality: 'collusion',
    accuracy: 0.5,
    vip: false,
    trapChance: 0.45,
    color: '#FF3D5A',
  },
  {
    id: 'whale',
    nameKey: 'room_whale',
    taglineKey: 'room_whale_tag',
    personality: 'collusion',
    accuracy: 0.62,
    vip: true,
    trapChance: 0.3,
    color: '#C77DFF',
  },
  {
    id: 'oracle',
    nameKey: 'room_oracle',
    taglineKey: 'room_oracle_tag',
    personality: 'honest',
    accuracy: 0.82,
    vip: true,
    trapChance: 0,
    color: '#F7931A',
  },
];

export function getLeadRoom(id: string): LeadRoomConfig | undefined {
  return LEAD_ROOMS.find(r => r.id === id);
}

export const LEAD_ROOM_CONFIG = {
  /** ms between a room's calls (a call is live for callLifetime, then resolves) */
  callIntervalMin: 40_000,
  callIntervalMax: 90_000,
  /** first call after fresh state (show the feature early) */
  firstCallDelayMin: 8_000,
  firstCallDelayMax: 25_000,
  /** how long a call stays "live" before its outcome resolves */
  callLifetime: 30_000,
  /** history entries kept per room (state size cap) */
  maxHistory: 5,
  /** VIP ad-gated entry stays unlocked this long (per room) */
  vipUnlockDuration: 300_000,
  /** call phrasing variants per direction (i18n call_up_N / call_down_N) */
  callVariants: 4,
} as const;

/**
 * Decide whether a call HITS, given the room's hidden accuracy and a roll.
 * Pure - deterministic in the roll so tests can assert the distribution.
 * @param roll uniform [0,1)
 */
export function callHits(accuracy: number, roll: number): boolean {
  return roll < accuracy;
}

/**
 * Decide whether a collusion room's call is a DUMP TRAP: it says "buy now"
 * (direction up) but the price is engineered to dump. Pure.
 * Only collusion rooms with trapChance > 0 can spring a trap, and only on
 * bullish calls (the "get in now" bait).
 * @param roll uniform [0,1)
 */
export function isTrapCall(
  cfg: Pick<LeadRoomConfig, 'personality' | 'trapChance'>,
  direction: 1 | -1,
  roll: number
): boolean {
  if (cfg.personality !== 'collusion' || cfg.trapChance <= 0) return false;
  if (direction !== 1) return false;
  return roll < cfg.trapChance;
}

/**
 * Resolve a call's real market direction.
 *  - trap calls always resolve DOWN (the buyer becomes exit liquidity) - a MISS.
 *  - otherwise a HIT means price went the called direction; a MISS the opposite.
 * Returns the direction the price "actually" went for display.
 */
export function resolveCallOutcome(
  claimedDirection: 1 | -1,
  hit: boolean,
  trap: boolean
): { actualDirection: 1 | -1; success: boolean } {
  if (trap) return { actualDirection: -1, success: false };
  const actualDirection = (hit ? claimedDirection : -claimedDirection) as 1 | -1;
  return { actualDirection, success: hit };
}
